import { z } from "zod";
import { MAX_STOCK_QTY_Q4 } from "@cx/domain";
import { listQuery } from "./common";
import { text } from "./fields";
import { ulidParam } from "./ids";

// ===== Conferência de estoque por leitura (RF-SUP-06, T3.2) =====

export const STOCK_COUNT_STATUSES = ["open", "applied", "canceled"] as const;
export type StockCountStatus = (typeof STOCK_COUNT_STATUSES)[number];

export const stockCountCreateInput = z.object({ warehouseId: ulidParam, notes: text(500) });
export type StockCountCreateInput = z.output<typeof stockCountCreateInput>;

/** Leitura: código de barras (GTIN), GTIN tributável ou SKU; quantidade padrão 1. */
export const stockCountReadInput = z.object({
  code: z.string("Informe o código").trim().min(1, "Informe o código").max(60, "Código longo demais"),
  qtyQ4: z
    .number("Informe a quantidade")
    .int("Quantidade inválida")
    .min(1, "Quantidade deve ser maior que zero")
    .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite")
    .default(10_000),
  device: text(60),
});
export type StockCountReadInput = z.output<typeof stockCountReadInput>;

/** Reiniciar: anula as leituras (todas, ou só as de um produto). */
export const stockCountResetInput = z.object({ productId: ulidParam.nullish() });

/** Aplicar: lança o balanço do que foi lido; `zeroUnread` zera também os produtos com saldo que não foram lidos. */
export const stockCountApplyInput = z.object({ zeroUnread: z.boolean().default(false) });
export type StockCountApplyInput = z.output<typeof stockCountApplyInput>;

export const stockCountListQuery = listQuery.omit({ q: true }).extend({
  warehouseId: ulidParam.optional(),
  status: z.enum(STOCK_COUNT_STATUSES, "Situação inválida").optional(),
});
export type StockCountListQuery = z.output<typeof stockCountListQuery>;

export type StockCountListItem = {
  id: string;
  number: number;
  warehouse: { id: string; name: string };
  status: StockCountStatus;
  readProducts: number;
  readQ4: number;
  createdAt: string;
  closedAt: string | null;
};

export type StockCountLine = {
  productId: string;
  name: string;
  sku: string | null;
  gtin: string | null;
  unit: string;
  countedQ4: number;
  /** Saldo físico atual no depósito (aplicada: o saldo antes do balanço). */
  expectedQ4: number;
  deltaQ4: number;
};

export type StockCountView = {
  id: string;
  number: number;
  warehouse: { id: string; name: string };
  status: StockCountStatus;
  notes: string | null;
  zeroUnread: boolean;
  createdAt: string;
  createdBy: string | null;
  closedAt: string | null;
  /** Produtos lidos (aberta) ou ajustados pelo balanço (aplicada). */
  lines: StockCountLine[];
  summary: {
    readQ4: number;
    readProducts: number;
    /** Produtos com saldo no depósito que não foram lidos. */
    unreadProducts: number;
    divergent: number;
  };
  /** Últimas leituras (mais nova primeiro). */
  reads: {
    id: string;
    productId: string;
    productName: string;
    code: string;
    qtyQ4: number;
    readAt: string;
  }[];
};

export type StockCountReadResult = {
  readId: string;
  product: { id: string; name: string; sku: string | null; unit: string };
  countedQ4: number;
};

export type StockCountApplyResult = { movements: number; adjusted: number };
